import { Injectable } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  DashboardMappingState,
  DashboardMappingStatus,
} from 'src/app/features/company-portal/modules/settings/models/MetabaseModels';
import { AnalyticsService } from 'src/app/features/company-portal/modules/settings/services/metabase.service';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsMenuService {
  private menuItemsSubject = new BehaviorSubject<DashboardMappingState[]>([]);
  public menuItems$ = this.menuItemsSubject.asObservable();

  private loaded = false;
  private loadedCompanyId: number | null = null;
  private loadedAppId: number | null = null;

  constructor(
    private analyticsService: AnalyticsService,
    private permissionsService: NgxPermissionsService,
  ) {}

  /**
   * Load (or reload) menu items. Cached per companyId+appId combo.
   * Individual items are filtered by the item's permissionKey if set.
   */
  loadMenuItems(companyId: number, appId: number, force = false): void {
    if (!force && this.loaded && this.loadedCompanyId === companyId && this.loadedAppId === appId) {
      return;
    }

    this.analyticsService.getIntegrationStatus(companyId).subscribe({
      next: (integration) => {
        if (!integration?.isConnected) {
          this.menuItemsSubject.next([]);
          this.loaded = true;
          return;
        }

        this.analyticsService.getDashboardMappings(companyId, appId).subscribe({
          next: (mappings) => {
            const userPerms = Object.keys(this.permissionsService.getPermissions() || {});
            const items = mappings
              .filter((m) => m.isEnabled && m.status === DashboardMappingStatus.Active)
              .filter((m) => !m.permissionKey || userPerms.includes(m.permissionKey))
              .sort((a, b) => a.sortOrder - b.sortOrder);

            this.menuItemsSubject.next(items);
            this.loaded = true;
            this.loadedCompanyId = companyId;
            this.loadedAppId = appId;
          },
          error: () => {
            this.menuItemsSubject.next([]);
          },
        });
      },
      error: () => {
        this.menuItemsSubject.next([]);
      },
    });
  }

  getMenuItems(): Observable<DashboardMappingState[]> {
    return this.menuItems$;
  }

  clearMenuItems(): void {
    this.loaded = false;
    this.loadedCompanyId = null;
    this.loadedAppId = null;
    this.menuItemsSubject.next([]);
  }
}
